import React from 'react'
import Agilite from 'agilite'
import { message, notification } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWifi,
  faExclamationTriangle,
  faTimesCircle,
  faInfoCircle,
  faCheckCircle,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons'
import LibEnums from './enums'
import Store from '../../store'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

// Error Types Classification
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  UNKNOWN: 'UNKNOWN',
  BUSINESS_LOGIC: 'BUSINESS_LOGIC'
}

// Error Severity Levels
export const ERROR_SEVERITY = {
  LOW: 'LOW', // Non-critical, user can continue
  MEDIUM: 'MEDIUM', // Important but not blocking
  HIGH: 'HIGH', // Blocks current action
  CRITICAL: 'CRITICAL' // Blocks entire application
}

// User-friendly error messages with actions
const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: {
    title: 'Connection Issue',
    message: 'Unable to connect to our servers. Please check your internet connection.',
    icon: faWifi,
    actions: ['Retry', 'Check Connection'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.AUTHENTICATION]: {
    title: 'Authentication Required',
    message: 'Your session has expired. Please log in again.',
    icon: faShieldAlt,
    actions: ['Login Again'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.AUTHORIZATION]: {
    title: 'Access Denied',
    message: "You don't have permission to perform this action.",
    icon: faTimesCircle,
    actions: ['Contact Support'],
    severity: ERROR_SEVERITY.MEDIUM
  },
  [ERROR_TYPES.VALIDATION]: {
    title: 'Invalid Information',
    message: 'Please check the information you entered and try again.',
    icon: faExclamationTriangle,
    actions: ['Review Form'],
    severity: ERROR_SEVERITY.LOW
  },
  [ERROR_TYPES.SERVER]: {
    title: 'Server Error',
    message: 'Our servers are experiencing issues. Please try again in a moment.',
    icon: faTimesCircle,
    actions: ['Retry', 'Contact Support'],
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.BUSINESS_LOGIC]: {
    title: 'Operation Not Allowed',
    message: 'This action cannot be completed due to business rules.',
    icon: faInfoCircle,
    actions: ['Review Requirements'],
    severity: ERROR_SEVERITY.MEDIUM
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: 'Unexpected Error',
    message: 'Something went wrong. Our team has been notified.',
    icon: faExclamationTriangle,
    actions: ['Retry', 'Contact Support'],
    severity: ERROR_SEVERITY.MEDIUM
  }
}

// Enhanced error logging
const logError = async (error, context = {}) => {
  try {
    const errorData = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message || 'Unknown error',
        stack: error.stack,
        name: error.name,
        code: error.code
      },
      context: {
        user: Store.getState().auth?.agiliteUser?.email || 'Anonymous',
        userAgent: navigator.userAgent,
        url: window.location.href,
        component: context.component,
        action: context.action,
        ...context
      },
      severity: classifyError(error).severity
    }

    await agilite.BatchLogging.createLogEntry(
      LibEnums.LOG_PROFILE_KEY,
      LibEnums.META_CLINIC,
      LibEnums.META_CLINIC_PORTAL,
      LibEnums.errorCodes[400],
      errorData.error.message,
      LibEnums.contentTypes.APPLICATION_JSON,
      errorData
    )
  } catch (loggingError) {
    console.error('Failed to log error:', loggingError)
  }
}

// Smart error classification
export const classifyError = (error) => {
  // Network errors
  if (
    !error.response &&
    (error.code === 'NETWORK_ERROR' ||
      error.message?.toLowerCase().includes('network') ||
      error.message?.toLowerCase().includes('fetch'))
  ) {
    return { type: ERROR_TYPES.NETWORK, ...ERROR_MESSAGES[ERROR_TYPES.NETWORK] }
  }

  // HTTP Response errors
  if (error.response) {
    const status = error.response.status

    switch (true) {
      case status === 401:
        return { type: ERROR_TYPES.AUTHENTICATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHENTICATION] }

      case status === 403:
        return { type: ERROR_TYPES.AUTHORIZATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHORIZATION] }

      case status >= 400 && status < 500:
        // Check if it's a validation error
        if (
          error.response.data?.validationErrors ||
          error.response.data?.message?.toLowerCase().includes('validation')
        ) {
          return { type: ERROR_TYPES.VALIDATION, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }
        }
        return { type: ERROR_TYPES.CLIENT, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }

      case status >= 500:
        return { type: ERROR_TYPES.SERVER, ...ERROR_MESSAGES[ERROR_TYPES.SERVER] }
    }
  }

  // Firebase Auth errors
  if (error.message?.includes('auth/')) {
    return { type: ERROR_TYPES.AUTHENTICATION, ...ERROR_MESSAGES[ERROR_TYPES.AUTHENTICATION] }
  }

  // Validation errors
  if (
    error.name === 'ValidationError' ||
    error.message?.toLowerCase().includes('validation') ||
    error.message?.toLowerCase().includes('required')
  ) {
    return { type: ERROR_TYPES.VALIDATION, ...ERROR_MESSAGES[ERROR_TYPES.VALIDATION] }
  }

  // Business logic errors
  if (
    error.message?.toLowerCase().includes('business') ||
    error.message?.toLowerCase().includes('rule') ||
    error.message?.toLowerCase().includes('not allowed')
  ) {
    return { type: ERROR_TYPES.BUSINESS_LOGIC, ...ERROR_MESSAGES[ERROR_TYPES.BUSINESS_LOGIC] }
  }

  // Default to unknown
  return { type: ERROR_TYPES.UNKNOWN, ...ERROR_MESSAGES[ERROR_TYPES.UNKNOWN] }
}

// Enhanced error handler with recovery options
export const handleError = (error, options = {}) => {
  const {
    showNotification = true,
    logError: shouldLog = true,
    context = {},
    customMessage = null,
    onRetry = null,
    component = 'Unknown'
  } = options

  // Classify the error
  const errorInfo = classifyError(error)

  // Log the error if needed
  if (shouldLog) {
    logError(error, { ...context, component })
  }

  // Use custom message if provided
  const displayMessage = customMessage || errorInfo.message

  console.error(`[${errorInfo.type}] ${component}:`, error)

  // Show appropriate notification based on severity
  if (showNotification) {
    const notificationConfig = {
      message: errorInfo.title,
      description: displayMessage,
      icon: <FontAwesomeIcon icon={errorInfo.icon} />,
      duration: errorInfo.severity === ERROR_SEVERITY.CRITICAL ? 0 : 4.5
    }

    // Add retry button if onRetry is provided
    if (onRetry && errorInfo.actions.includes('Retry')) {
      notificationConfig.btn = (
        <button
          onClick={() => {
            notification.close('error-notification')
            onRetry()
          }}
          style={{
            background: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 12px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      )
    }

    switch (errorInfo.severity) {
      case ERROR_SEVERITY.CRITICAL:
        notification.error({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.HIGH:
        notification.error({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.MEDIUM:
        notification.warning({ ...notificationConfig, key: 'error-notification' })
        break
      case ERROR_SEVERITY.LOW:
        message.warning(displayMessage)
        break
      default:
        notification.info({ ...notificationConfig, key: 'error-notification' })
    }
  }

  return {
    type: errorInfo.type,
    message: displayMessage,
    severity: errorInfo.severity,
    actions: errorInfo.actions,
    originalError: error
  }
}

// Specific error handlers for common scenarios
export const handleNetworkError = (onRetry) => {
  return handleError(new Error('Network connection failed'), {
    context: { type: 'network' },
    onRetry
  })
}

export const handleValidationError = (fieldErrors) => {
  const message = Object.values(fieldErrors).join('. ')
  return handleError(new Error(message), {
    customMessage: `Please correct the following: ${message}`,
    context: { type: 'validation', fields: Object.keys(fieldErrors) }
  })
}

export const handleAPIError = (error, context = {}) => {
  return handleError(error, {
    context: { type: 'api', ...context },
    component: context.component || 'API'
  })
}

// React Error Boundary helper
export const createErrorBoundary = (fallbackComponent) => {
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props)
      this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
      handleError(error, {
        context: {
          type: 'react-boundary',
          errorInfo: errorInfo.componentStack
        },
        component: 'ErrorBoundary'
      })
    }

    render() {
      if (this.state.hasError) {
        return fallbackComponent ? (
          fallbackComponent(this.state.error)
        ) : (
          <div>Something went wrong. Please refresh the page.</div>
        )
      }

      return this.props.children
    }
  }

  return ErrorBoundary
}

// Hook for handling errors in components
export const useErrorHandler = (component = 'Component') => {
  const handleComponentError = (error, context = {}) => {
    return handleError(error, {
      ...context,
      component
    })
  }

  return { handleError: handleComponentError }
}

export default {
  handleError,
  handleNetworkError,
  handleValidationError,
  handleAPIError,
  classifyError,
  createErrorBoundary,
  useErrorHandler,
  ERROR_TYPES,
  ERROR_SEVERITY
}
